/**
 * /admin/migrations — Super-admin one-shot migration runner.
 *
 * Single page. No terminal. Authenticated via the same admin cookie used
 * everywhere else. Asks the operator to paste the production
 * MIGRATION_TOKEN once, then exposes two buttons:
 *   • Dry run  → POST /api/admin/migrations/repair-listing-regions?dry=true
 *   • Apply    → POST .../repair-listing-regions?dry=false
 * Shows the JSON response inline, plus a per-collection table.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Loader2, Shield, AlertTriangle, CheckCircle2, ArrowLeft, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import Nav from '@/components/Nav';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLLECTION_LABELS = {
  partner_properties: 'Properties',
  partner_cars: 'Cars',
  partner_yachts: 'Yachts',
  partner_portfolio: 'Designer Portfolio',
  partner_interior_designs: 'Interior Designs (legacy)',
  partner_services: 'Services',
};

const AdminMigrationsPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [token, setToken] = useState('');
  const [running, setRunning] = useState(false);
  const [mode, setMode] = useState(null); // 'dry' | 'apply'
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    const role = (user.role || '').toLowerCase();
    if (role !== 'super_admin' && role !== 'admin') {
      toast.error('Super-admin access required.');
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  const run = async (dry) => {
    if (!token.trim()) {
      toast.error('Please paste the MIGRATION_TOKEN first.');
      return;
    }
    setRunning(true);
    setMode(dry ? 'dry' : 'apply');
    setResult(null);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/migrations/repair-listing-regions`,
        null,
        { params: { token: token.trim(), dry } },
      );
      setResult(data);
      toast.success(dry ? 'Dry run complete — review the summary below.' : 'Migration applied successfully.');
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || 'Migration failed';
      setError(detail);
      toast.error(detail);
    } finally {
      setRunning(false);
    }
  };

  const totals = result?.summary
    ? Object.values(result.summary).reduce(
        (acc, s) => ({
          checked: acc.checked + (s.checked || 0),
          mismatches: acc.mismatches + (s.mismatches || 0),
          fixed: acc.fixed + (s.fixed || 0),
        }),
        { checked: 0, mismatches: 0, fixed: 0 },
      )
    : null;

  return (
    <div className="min-h-screen bg-obsidian text-text-primary" data-testid="admin-migrations-page">
      <Nav variant="admin" />
      <div className="max-w-[1100px] mx-auto px-4 sm:px-8 py-10">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-text-secondary hover:text-gold text-sm mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex items-start gap-4 mb-8">
          <div className="w-12 h-12 rounded-xl bg-gold/10 ring-1 ring-gold/20 flex items-center justify-center">
            <Wrench className="w-5 h-5 text-gold" />
          </div>
          <div>
            <h1 className="font-playfair text-3xl md:text-4xl font-semibold mb-1">One-Shot Migrations</h1>
            <p className="text-text-secondary text-sm max-w-2xl">
              Super-admin only. Each script here is <span className="text-emerald-300">idempotent</span> — running it twice on a
              consistent database is a no-op. The dry run never writes; the apply run writes only the columns explicitly listed.
            </p>
          </div>
        </div>

        {/* Migration card */}
        <div className="glass-card p-6 sm:p-8 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-4 h-4 text-gold" />
            <h2 className="font-playfair text-xl font-semibold">Repair Listing Regions</h2>
          </div>
          <p className="text-text-secondary text-sm mb-5">
            Realigns each partner asset's <code className="text-gold">region</code>, <code className="text-gold">country_id</code>, and{' '}
            <code className="text-gold">currency</code> with the owning partner's bound region. Fixes the &ldquo;PRANEETH PRANAV SOLITAIRE
            showing up in UAE portal&rdquo; class of bug. Touches NO other field.
          </p>

          <div className="mb-5">
            <label className="text-xs uppercase tracking-[0.18em] text-text-secondary mb-1.5 block">
              Migration Token
            </label>
            <Input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Paste your production MIGRATION_TOKEN"
              className="bg-white/5 border-white/10 font-mono"
              data-testid="migration-token-input"
              autoComplete="off"
            />
            <p className="text-text-secondary text-[11px] mt-1.5">
              This is the same secret you set in production env vars. We never store it.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => run(true)}
              disabled={running}
              variant="outline"
              className="flex-1 border-white/20 hover:border-gold hover:text-gold"
              data-testid="dry-run-btn"
            >
              {running && mode === 'dry'
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running dry…</>
                : <>1. Dry Run (preview changes)</>
              }
            </Button>
            <Button
              onClick={() => run(false)}
              disabled={running || !result || result.dry === false}
              className="flex-1 bg-gold text-black hover:bg-gold-light disabled:opacity-40"
              data-testid="apply-btn"
            >
              {running && mode === 'apply'
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Applying…</>
                : <>2. Apply (write to DB)</>
              }
            </Button>
          </div>
          {!result && !running && (
            <p className="text-text-secondary text-[11px] mt-3">
              Tip: always click <span className="text-gold">Dry Run</span> first. The Apply button stays disabled until you've previewed the changes.
            </p>
          )}
        </div>

        {/* Error */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-5 mb-6 border-red-500/30"
            data-testid="error-card"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-300 mb-1">Migration failed</h3>
                <p className="text-text-secondary text-sm">{error}</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Result */}
        {result && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid="result-card">
            <div className="glass-card p-6 mb-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className={`w-5 h-5 ${result.dry ? 'text-amber-300' : 'text-emerald-300'}`} />
                  <h3 className="font-playfair text-lg font-semibold">
                    {result.dry ? 'Dry run summary' : 'Migration applied'}
                  </h3>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.15em] font-semibold ring-1 ${
                      result.dry
                        ? 'bg-amber-500/10 text-amber-300 ring-amber-500/30'
                        : 'bg-emerald-500/10 text-emerald-300 ring-emerald-500/30'
                    }`}
                  >
                    {result.dry ? 'DRY' : 'APPLIED'}
                  </span>
                </div>
                <div className="text-text-secondary text-xs">
                  Operator: <span className="text-white/80">{result.operator}</span>
                </div>
              </div>

              {/* Totals */}
              {totals && (
                <div className="grid grid-cols-3 gap-4 mb-5">
                  <div className="bg-white/[0.03] ring-1 ring-white/5 rounded-lg p-4 text-center">
                    <div className="text-xs uppercase tracking-[0.18em] text-text-secondary mb-1">Checked</div>
                    <div className="text-2xl font-playfair text-gold" data-testid="total-checked">{totals.checked}</div>
                  </div>
                  <div className="bg-white/[0.03] ring-1 ring-white/5 rounded-lg p-4 text-center">
                    <div className="text-xs uppercase tracking-[0.18em] text-text-secondary mb-1">Mismatches</div>
                    <div className="text-2xl font-playfair text-amber-300" data-testid="total-mismatches">{totals.mismatches}</div>
                  </div>
                  <div className="bg-white/[0.03] ring-1 ring-white/5 rounded-lg p-4 text-center">
                    <div className="text-xs uppercase tracking-[0.18em] text-text-secondary mb-1">Fixed</div>
                    <div className="text-2xl font-playfair text-emerald-300" data-testid="total-fixed">{totals.fixed}</div>
                  </div>
                </div>
              )}

              {/* Per-collection table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-text-secondary text-[11px] uppercase tracking-[0.16em]">
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 px-2">Collection</th>
                      <th className="text-right py-2 px-2">Checked</th>
                      <th className="text-right py-2 px-2">Mismatches</th>
                      <th className="text-right py-2 px-2">Fixed</th>
                      <th className="text-right py-2 px-2">Orphan</th>
                      <th className="text-right py-2 px-2">Unbound</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(result.summary || {}).map(([coll, s]) => (
                      <tr key={coll} className="border-b border-white/5" data-testid={`row-${coll}`}>
                        <td className="py-2 px-2 text-white/90">
                          {COLLECTION_LABELS[coll] || coll}
                          <div className="text-[10px] text-text-secondary font-mono">{coll}</div>
                        </td>
                        <td className="py-2 px-2 text-right">{s.checked}</td>
                        <td className={`py-2 px-2 text-right ${s.mismatches > 0 ? 'text-amber-300 font-semibold' : ''}`}>{s.mismatches}</td>
                        <td className={`py-2 px-2 text-right ${s.fixed > 0 ? 'text-emerald-300 font-semibold' : ''}`}>{s.fixed}</td>
                        <td className="py-2 px-2 text-right text-text-secondary">{s.orphan}</td>
                        <td className="py-2 px-2 text-right text-text-secondary">{s.partner_unbound}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {result.dry && totals && totals.mismatches > 0 && (
                <div className="mt-5 p-4 rounded-lg bg-amber-500/5 ring-1 ring-amber-500/20 text-sm text-amber-200">
                  Review the mismatches above. When you're ready, click <span className="text-gold font-semibold">Apply</span> to write
                  the fixes.
                </div>
              )}
              {result.dry && totals && totals.mismatches === 0 && (
                <div className="mt-5 p-4 rounded-lg bg-emerald-500/5 ring-1 ring-emerald-500/20 text-sm text-emerald-200">
                  No mismatches detected — your data is already consistent. No need to click Apply.
                </div>
              )}
              {!result.dry && (
                <div className="mt-5 p-4 rounded-lg bg-emerald-500/5 ring-1 ring-emerald-500/20 text-sm text-emerald-200">
                  Migration applied. Send a screenshot of this summary back to the engineering channel to close issue #3.
                </div>
              )}
            </div>

            {/* Raw JSON for the paranoid */}
            <details className="glass-card p-5">
              <summary className="cursor-pointer text-text-secondary text-sm hover:text-gold">Raw JSON response</summary>
              <pre className="text-xs text-text-secondary mt-3 overflow-x-auto bg-black/30 p-3 rounded-lg">
                {JSON.stringify(result, null, 2)}
              </pre>
            </details>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AdminMigrationsPage;
